import { observer } from "mobx-react-lite";
import { logger } from "../../common/logger";
import type { Product } from "../../common/types/productTypes";
import { type ProductSearchResults, SEARCH_TYPE, type SearchComponentProps } from "../../common/types/searchTypes";
import useSupportedLocale from "../../common/utils/useSupportedLocale";
import featuresStore from "../../stores/features/featuresStore";
import { prioritizeAgreementAssortment } from "../../stores/product/productUtils";
import searchStore from "../../stores/search/searchStore";
import { InternalLink } from "../Links";
import ProductHit from "./ProductHit";

const hide = () => searchStore.clear(SEARCH_TYPE.QuickSearch, true);

const HitsForProduct = ({ searchDomain, maxHits = Number.MAX_VALUE }: SearchComponentProps) => {
   const locale = useSupportedLocale();

   const productResults: ProductSearchResults | undefined = searchDomain
      ? (searchStore[SEARCH_TYPE.QuickSearch].results.find(
           (result) => result.name === searchDomain.name && searchDomain.type === "product"
        ) as ProductSearchResults | undefined)
      : undefined;

   if (!productResults) {
      return null;
   }

   const sortedProducts = prioritizeAgreementAssortment(productResults.products, featuresStore.prioritizeAgreementAssortment);

   const queryID = productResults.queryID;
   const products = sortedProducts.filter((p) => p).slice(0, maxHits);
   const productsCount = products?.length;
   const hasProducts = !!products?.length;

   return (
      <>
         {hasProducts && (
            <div className="flex w-full flex-col gap-2">
               <div>
                  <h3 className="pb-2">{searchDomain.displayName(locale)}</h3>
               </div>
               <div className="grid gap-y-2 lg:grid-cols-3 lg:gap-x-8">
                  {products.map((product: Product, i: number) => {
                     const hit = productResults.hits.find((h) => h.sku === product.sku);
                     if (!hit) {
                        logger.warn("search", `Products contained product not available in search result, ${product.sku}`);
                        return null;
                     }
                     return (
                        <ProductHit
                           key={product.sku}
                           hit={hit}
                           product={product}
                           position={i + 1}
                           algoliaIndex={searchDomain?.indexName || ""}
                           queryID={queryID}
                           numberOfHits={productsCount}
                        />
                     );
                  })}
               </div>
               <div>
                  <InternalLink
                     className="my-2 inline-block p-2"
                     onClick={() => hide()}
                     href={`/search/${searchDomain.name}?q=${productResults.query}`}
                  >{`${searchDomain.linkText(locale)} med '${productResults.query}'`}</InternalLink>
               </div>
            </div>
         )}
      </>
   );
};

export default observer(HitsForProduct);
